import { Component, OnInit, computed, inject } from '@angular/core';
import { DateTime } from 'luxon';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { BankStore, ContactStore } from '../../../core/stores/base.store';
import { Loan, BankTransaction, Bank } from '../../../core/models';
import { LoanUtils } from '../../../core/utils/loan.utils';
import { parseYYYYMMDDToDate, formatDateAsYYYYMMDD } from '../../../core/utils/date.utils';
import { LoanPaymentDialogComponent } from '../loan-payment-dialog/loan-payment-dialog.component';

@Component({
  selector: 'app-amortization',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './amortization.component.html',
  styleUrls: ['./amortization.component.css']
})
export class AmortizationComponent implements OnInit {
  private router = inject(Router);
  private bankStore = inject(BankStore);
  private contactStore = inject(ContactStore);
  private dialog = inject(MatDialog);

  selectedLoan = computed(() => this.bankStore.selectedLoan());
  
  // Add computed signals for specific loan properties to ensure they're always up-to-date
  loanName = computed(() => this.selectedLoan()?.name);
  loanAmount = computed(() => this.selectedLoan()?.amount);
  loanInterestRate = computed(() => this.selectedLoan()?.interest_rate);
  loanTerm = computed(() => this.selectedLoan()?.term);
  loanFrequency = computed(() => this.selectedLoan()?.frequency);
  loanPayment = computed(() => this.selectedLoan()?.payment);
  loanCloseDate = computed(() => this.selectedLoan()?.close_date);
  loanRemainingBalance = computed(() => this.selectedLoan()?.remaining_balance);
  loanNextPaymentDate = computed(() => this.selectedLoan()?.next_payment_date);
  loanNextPaymentNumber = computed(() => this.selectedLoan()?.next_payment_number);
  
  contact = computed(() => {
    const loan = this.selectedLoan();
    if (!loan) return null;
    return this.contactStore.entities().find(c => c.id === loan.contact_id) || null;
  });

  // Get actual transactions for this loan
  actualTransactions = computed(() => {
    const loan = this.selectedLoan();
    if (!loan || !this.bankStore.selected()) return [];
    
    return (this.bankStore.selected()?.bank_transactions_collection || [])
      .filter(t => t.loan_id === loan.id)
      .sort((a, b) => {
        const dateA = parseYYYYMMDDToDate(a.transaction_date) || new Date();
        const dateB = parseYYYYMMDDToDate(b.transaction_date) || new Date();
        return dateA.getTime() - dateB.getTime();
      });
  });

  // Generate amortization schedule
  amortizationSchedule = computed(() => {
    const loan = this.selectedLoan();
    if (!loan) return [];

    // Generate the theoretical amortization schedule using LoanUtils
    const schedule = LoanUtils.generateAmortizationSchedule(loan);
    
    // Merge with actual transactions and get the updated schedule
    const mergedSchedule = this.mergeWithActualTransactions(schedule, this.actualTransactions());
    
    // Update the loan's remaining balance based on the actual payments
    this.updateLoanRemainingBalance(mergedSchedule);
    
    return mergedSchedule;
  });
  
  /**
   * Update the loan's remaining balance based on the actual payments in the amortization schedule
   * This ensures that future amortization calculations properly account for extra payments
   * @param schedule The merged amortization schedule with actual payments
   */
  private updateLoanRemainingBalance(schedule: any[]): void {
    const loan = this.selectedLoan();
    if (!loan) return;
    
    // Find all actual payments (excluding funding)
    const actualPayments = schedule.filter(p => p.isActual && p.paymentNumber > 0);
    if (actualPayments.length === 0) return;
    
    // Sort by date, descending to get the most recent payment
    actualPayments.sort((a, b) => {
      const dateA = a.actualDate || a.paymentDate;
      const dateB = b.actualDate || b.paymentDate;
      return dateB.getTime() - dateA.getTime();
    });
  }

  displayedColumns: string[] = [
    'paymentNumber',
    'paymentDate',
    'payment',
    'principal',
    'interest',
    'lateFee',
    'balance',
    'state'
  ];

  ngOnInit(): void {
    if (!this.selectedLoan()) {
      this.router.navigate(['/banks']);
    }
  }

  goBack(): void {
    this.router.navigate(['/banks']);
  }

  // This method is no longer needed as we're using LoanUtils.generateAmortizationSchedule
  // Keeping it commented for reference
  /*
  private generateAmortizationSchedule(loan: Loan): any[] {
    // This functionality has been moved to LoanUtils.generateAmortizationSchedule
    return LoanUtils.generateAmortizationSchedule(loan);
  }
  */

  private mergeWithActualTransactions(schedule: any[], transactions: BankTransaction[]): any[] {
    // Create a copy of the schedule without modifying the original
    const mergedSchedule = [...schedule];
    
    // Keep track of which payment numbers have been processed
    const processedPaymentNumbers = new Set();
    
    // First, find the funding transaction (loan creation)
    const fundingTransaction = transactions.find(t => 
      t.loan_id === this.selectedLoan()?.id && 
      t.amount > 0 && // Positive amount for funding (money received)
      t.category === 'Loan funding');
      
    if (fundingTransaction) {
      // Find the funding row in the schedule (payment number 0)
      const fundingIndex = mergedSchedule.findIndex(p => p.paymentNumber === 0);
      if (fundingIndex !== -1) {
        // Mark the funding row as an actual transaction
        mergedSchedule[fundingIndex] = {
          ...mergedSchedule[fundingIndex],
          payment: -fundingTransaction.amount, // Keep as negative (money received)
          isActual: true,
          actualDate: parseYYYYMMDDToDate(fundingTransaction.transaction_date) || new Date()
        };
        processedPaymentNumbers.add(0);
      }
    }
    
    // Add actual transactions (only loan payments, not funding)
    const loanPayments = transactions.filter(t => 
      t.loan_id === this.selectedLoan()?.id && 
      t.category === 'Loan payment');
    
    // Sort loan payments by date to ensure they're processed in chronological order
    loanPayments.sort((a, b) => {
      const dateA = parseYYYYMMDDToDate(a.transaction_date) || new Date();
      const dateB = parseYYYYMMDDToDate(b.transaction_date) || new Date();
      return dateA.getTime() - dateB.getTime();
    });
      
    loanPayments.forEach(transaction => {
      // Parse the transaction date using the utility function
      const transactionDate = parseYYYYMMDDToDate(transaction.transaction_date) || new Date();
      
      // If transaction has a loan_amortization_number, use it to find the corresponding scheduled payment
      if (transaction.loan_amortization_number !== null && transaction.loan_amortization_number !== undefined) {
        // Convert loan_amortization_number to a clean integer if it's a whole number
        let paymentNumber = transaction.loan_amortization_number;
        if (paymentNumber % 1 === 0) {
          paymentNumber = Math.floor(paymentNumber);
        }
        // Check if the loan_amortization_number is an integer (regular payment) or has decimal (extra payment)
        const isExtraPayment = paymentNumber % 1 !== 0;
        
        if (isExtraPayment) {
          // This is an extra payment with a non-integer amortization number (e.g., 1.5)
          mergedSchedule.push({
            paymentNumber: paymentNumber,
            paymentDate: transactionDate,
            payment: transaction.amount,
            principal: transaction.principal || 0,
            interest: transaction.interest || 0,
            lateFee: transaction.late_fee || 0,
            balance: 0, // This will be recalculated
            state: 'Extra Payment',
            isActual: true,
            actualDate: transactionDate
          });
        } else {
          // This is a regular scheduled payment with an integer amortization number
          // Find the scheduled payment with the matching payment number
          const scheduledPaymentIndex = mergedSchedule.findIndex(payment => 
            payment.paymentNumber === paymentNumber);
          
          // Mark this payment number as processed
          processedPaymentNumbers.add(paymentNumber);
          
          if (scheduledPaymentIndex !== -1) {
            // Get the payment from the schedule
            const payment = mergedSchedule[scheduledPaymentIndex];
            
            // Determine if this is an on-time payment or a late payment
            const dueDate = payment.paymentDate;
            const isPaid = payment.isActual === true;
            const isLate = transactionDate.getTime() > dueDate.getTime() + (86400000 * 5); // 5 days grace period
            
            // Set appropriate state
            let newState = 'Paid';
            if (payment.paymentNumber === 0) {
              newState = 'Funding';
            } else if (isLate) {
              newState = 'Paid Late';
            }
            
            // Don't replace already paid payments
            if (!isPaid) {
              // Replace the scheduled payment with the actual one
              mergedSchedule[scheduledPaymentIndex] = {
                ...payment,
                payment: transaction.amount,
                principal: transaction.principal || 0,
                interest: transaction.interest || 0,
                lateFee: transaction.late_fee || 0,
                state: newState,
                isActual: true,
                actualDate: transactionDate
              };
            }
          } else {
            // Integer amortization number but no matching scheduled payment
            // This could happen if the loan term was shortened or the schedule was regenerated
            // Add it as a regular paid payment
            mergedSchedule.push({
              paymentNumber: paymentNumber,
              paymentDate: transactionDate,
              payment: transaction.amount,
              principal: transaction.principal || 0,
              interest: transaction.interest || 0,
              lateFee: transaction.late_fee || 0,
              balance: 0, // This will be recalculated
              state: 'Paid',
              isActual: true,
              actualDate: transactionDate
            });
          }
        }
      } else {
        // No loan_amortization_number, use the old method as fallback
        // Find the closest scheduled payment
        const closestIndex = this.findClosestScheduledPayment(schedule, transactionDate);
        
        if (closestIndex !== -1) {
          // Get the payment from the schedule
          const payment = mergedSchedule[closestIndex];
          
          // Only update state to 'Paid' if it's not the funding row (payment number 0)
          const newState = payment.paymentNumber === 0 ? 'Funding' : 'Paid';
          
          // Replace the scheduled payment with the actual one
          mergedSchedule[closestIndex] = {
            ...payment,
            payment: transaction.amount,
            principal: transaction.principal || 0,
            interest: transaction.interest || 0,
            lateFee: transaction.late_fee || 0,
            state: newState,
            isActual: true,
            actualDate: transactionDate
          };
        } else {
          // Add as an extra payment
          // Use integer payment number for the next available payment rather than fractional
          let nextPaymentNumber = 1;
          const existingPaymentNumbers = mergedSchedule.map(p => p.paymentNumber);
          while (existingPaymentNumbers.includes(nextPaymentNumber)) {
            nextPaymentNumber++;
          }
          
          mergedSchedule.push({
            paymentNumber: nextPaymentNumber,
            paymentDate: transactionDate,
            payment: transaction.amount,
            principal: transaction.principal || 0,
            interest: transaction.interest || 0,
            lateFee: transaction.late_fee || 0,
            balance: 0, // This will be recalculated
            state: 'Extra Payment',
            isActual: true,
            actualDate: transactionDate
          });
        }
      }
    });
    
    // Remove scheduled payments that have been replaced by actual payments
    const filteredSchedule = mergedSchedule.filter(payment => {
      // Keep all actual payments
      if (payment.isActual) return true;
      
      // For scheduled payments, only keep those that haven't been processed
      return !processedPaymentNumbers.has(payment.paymentNumber);
    });
    
    // Sort by payment date and then by payment number for payments on the same date
    filteredSchedule.sort((a, b) => {
      const dateA = a.actualDate || a.paymentDate;
      const dateB = b.actualDate || b.paymentDate;
      
      // First sort by date
      const dateDiff = dateA.getTime() - dateB.getTime();
      if (dateDiff !== 0) return dateDiff;
      
      // If dates are the same, sort by payment number
      return a.paymentNumber - b.paymentNumber;
    });
    
    // Recalculate balances based on the actual payment sequence
    let runningBalance = this.selectedLoan()?.amount || 0;
    
    // Track the highest payment number we've seen for actual payments
    let highestActualPaymentNumber = 0;
    
    filteredSchedule.forEach(payment => {
      // Skip the funding row when calculating balances
      if (payment.paymentNumber === 0) {
        payment.balance = runningBalance;
        return;
      }
      
      // Update the running balance by subtracting the principal
      if (payment.principal) {
        runningBalance -= payment.principal;
      }
      
      // Set the balance for this payment
      payment.balance = Math.max(0, runningBalance);
      
      // Track the highest payment number for actual payments
      if (payment.isActual && payment.paymentNumber > highestActualPaymentNumber) {
        highestActualPaymentNumber = payment.paymentNumber;
      }
    });
    
    // We don't need to update the loan's remaining balance here anymore
    // The updateLoanRemainingBalance method will handle this after the schedule is generated
    
    return filteredSchedule;
  }

  private findClosestScheduledPayment(schedule: any[], date: Date): number {
    const dateTime = date.getTime();
    let closestIndex = -1;
    let minDiff = Number.MAX_VALUE;
    
    schedule.forEach((payment, index) => {
      const paymentTime = payment.paymentDate.getTime();
      const diff = Math.abs(paymentTime - dateTime);
      
      // Consider it a match if within 15 days
      if (diff < 15 * 24 * 60 * 60 * 1000 && diff < minDiff) {
        minDiff = diff;
        closestIndex = index;
      }
    });
    
    return closestIndex;
  }

  private findPaymentNumber(schedule: any[], date: Date): number {
    // Find the payment number based on the date
    for (let i = 0; i < schedule.length; i++) {
      const paymentDateTime = schedule[i].paymentDate.getTime();
      if (date.getTime() < paymentDateTime) {
        // Return the previous payment number if this isn't the first payment
        // Otherwise return 1 for the first payment
        return Math.max(1, i);
      }
    }
    // After all scheduled payments, use the next available integer
    // Get all existing payment numbers
    const paymentNumbers = schedule.map(p => p.paymentNumber);
    let nextNumber = schedule.length;
    while (paymentNumbers.includes(nextNumber)) {
      nextNumber++;
    }
    return nextNumber;
  }

  /**
   * Get the total interest paid over the life of the loan
   */
  getTotalInterest(loan: Loan | null): number {
    if (!loan) return 0;
    
    const schedule = this.amortizationSchedule();
    if (!schedule || !schedule.length) return 0;
    
    // Check if there are any actual loan payments (not funding)
    const hasActualPayments = schedule.some(payment => 
      payment.state !== 'Funding' && 
      payment.isActual === true && 
      LoanUtils.parseFormattedNumber(payment.payment) > 0);
    
    // If no actual payments have been made, return 0
    if (!hasActualPayments) return 0;
    
    return schedule.reduce((total, payment) => {
      // Skip the funding row, non-actual payments, and payments with non-positive amounts
      if (payment.state === 'Funding' || 
          !payment.isActual || 
          LoanUtils.parseFormattedNumber(payment.payment) <= 0) {
        return total;
      }
      
      // Ensure interest is a number using LoanUtils.parseFormattedNumber
      const interestAmount = LoanUtils.parseFormattedNumber(payment.interest);
      return total + interestAmount;
    }, 0);
  }

  /**
   * Get the total amount paid over the life of the loan (principal + interest)
   */
  /**
   * Get the total amount paid over the life of the loan (principal + interest)
   */
  getTotalPaid(loan: Loan | null): number {
    if (!loan) {
      return 0;
    }
    
    const schedule = this.amortizationSchedule();
    if (!schedule || !schedule.length) {
      return 0;
    }
    
    // Check if there are any actual loan payments (not funding)
    const hasActualPayments = schedule.some(payment => 
      payment.state !== 'Funding' && 
      payment.isActual === true && 
      LoanUtils.parseFormattedNumber(payment.payment) > 0);
    
    if (!hasActualPayments) {
      return 0;
    }
    
    const totalPaid = schedule.reduce((total, payment) => {
      // Skip the funding row, non-actual payments, and payments with non-positive amounts
      if (payment.state === 'Funding' || 
          !payment.isActual || 
          LoanUtils.parseFormattedNumber(payment.payment) <= 0) {
        return total;
      }
      
      // Ensure payment is a number using LoanUtils.parseFormattedNumber
      const paymentAmount = LoanUtils.parseFormattedNumber(payment.payment);
      return total + paymentAmount;
    }, 0);
    
    return totalPaid;
  }

  /**
   * Open the loan payment dialog with prepopulated data
   */
  openPaymentDialog(): void {
    const loan = this.selectedLoan();
    if (!loan) return;
    
    const bank = this.bankStore.selected();
    if (!bank) return;
    
    // Create a deep copy of the loan to avoid reference issues
    // Ensure numeric values are properly preserved
    const loanCopy = {
      ...loan,
      // Ensure these are numbers
      payment: typeof loan.payment === 'number' ? loan.payment : parseFloat(String(loan.payment)),
      amount: typeof loan.amount === 'number' ? loan.amount : parseFloat(String(loan.amount)),
      interest_rate: typeof loan.interest_rate === 'number' ? loan.interest_rate : parseFloat(String(loan.interest_rate)),
      remaining_balance: typeof loan.remaining_balance === 'number' ? loan.remaining_balance : parseFloat(String(loan.remaining_balance)),
      next_payment_number: typeof loan.next_payment_number === 'number' ? loan.next_payment_number : parseFloat(String(loan.next_payment_number))
    };
        
    // Open the dialog with the loan and bank data
    const dialogRef = this.dialog.open(LoanPaymentDialogComponent, {
      width: '500px',
      data: { loan: loanCopy, bank }
    });
    
    // Handle the dialog close event
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.transaction) {
          // Ensure the transaction date is in the correct format
          const transaction = {
            ...result.transaction,
            transaction_date: formatDateAsYYYYMMDD(result.transaction.transaction_date)
          };
          
          this.bankStore.createChild({
            child: transaction,
            endpoint: 'bank_transaction',
            collectionName: 'bank_transactions_collection'
          });
        }
        
        if (result.updatedLoan) {
          // Ensure all dates in the loan are in the correct format
          const updatedLoan = {
            ...result.updatedLoan,
            next_payment_date: formatDateAsYYYYMMDD(result.updatedLoan.next_payment_date),
          };
          
          // Update the loan in the store
          this.bankStore.updateLoan({
            loan: updatedLoan,
            endpoint: 'loan',
            collectionName: 'loans_collection'
          });
          
          // Force a refresh of the selected loan to ensure the UI updates
          // First deselect the loan, then reselect it with the updated data
          this.bankStore.selectLoan(null);
          setTimeout(() => {
            this.bankStore.selectLoan(updatedLoan);
          }, 0);
        }
      }
    });
  }
}
